import KitLoader from '@omni/kit/components/KitLoader';
import { useScreenContext } from '@omni/kit/contexts/ScreenContext';
import LottieView, { LottieViewProps } from '@omni/kit/lottie';
import { getSpacing } from '@omni/kit/theming/SpacingType';
import React, { useEffect, useState } from 'react';
import { Platform, StyleSheet, View, ViewStyle } from 'react-native';
import WebView, { WebViewProps } from 'react-native-webview';

import { useBlockPageContext } from '../../BlockPageContext';
import BackgroundExtension from '../../components/BackgroundExtension';
import { IBlockProps } from '../types';

const debug = require('debug')('tca:blocks:BrowserBlock');

export interface BrowserBlockProps extends IBlockProps {
  /**
   * Height of the WebView container.
   *
   * - number: Fixed height in pixels (e.g., `400`)
   * - percentage (string): Percentage of viewport height (e.g., `"100%"` uses full viewport height, the area between the top and bottom system UI/bars)
   *
   * Defaults to `"100%"`. When `fitContent` is true, this value is used as the initial height while measuring content.
   */
  height?: number | `${number}%`;

  /**
   * When true, the WebView height is dynamically adjusted to match the content height
   * from 'fitContentHeight' messages sent by the web page.
   * The `height` prop is used as the initial height while measuring content.
   *
   * **Web Implementation:**
   * Web pages must send postMessage to both platforms for cross-platform compatibility:
   *
   * ```javascript
   * const message = { type: 'fitContentHeight', height: calculatedHeight };
   *
   * // For React Native WebView (mobile)
   * if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
   *   window.ReactNativeWebView.postMessage(JSON.stringify(message));
   * }
   *
   * // For web iframe
   * if (window.parent && window.parent !== window) {
   *   window.parent.postMessage(message, '*');
   * }
   * ```
   *
   * The web page is responsible for:
   * - Calculating its own content height (including margins, padding, etc.)
   * - Sending the initial height message when loaded
   * - Sending updated height messages when content changes dynamically
   * - Supporting both React Native WebView and web iframe communication
   *
   * Supported in release 6.15+
   */
  fitContent?: boolean;

  /**
   * When true, the WebView will handle 'dispatchAction' messages sent by the web page.
   * This allows web content to trigger actions in the Subsplash app.
   *
   * **Web Implementation:**
   * Web pages must send postMessage to both platforms for cross-platform compatibility:
   *
   * ```javascript
   * const message = { type: 'dispatchAction', action: actionObject };
   *
   * // For React Native WebView (mobile)
   * if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
   *   window.ReactNativeWebView.postMessage(JSON.stringify(message));
   * }
   *
   * // For web iframe
   * if (window.parent && window.parent !== window) {
   *   window.parent.postMessage(message, '*');
   * }
   * ```
   *
   * Example action objects:
   * - `{ handler: 'app', command: 'reloadActiveScreen' }`
   * - `{ handler: 'app', command: 'closeActiveScreen' }`
   * - `{ handler: 'blockPage', type: 'content', url: '/path/to/content.json' }`
   *
   * Supported in release 6.15+
   */
  actionDispatch?: boolean;

  /**
   * @deprecated Use webViewProps.scrollEnabled instead. Will be removed in a future release.
   * Deprecated in release 6.15+
   */
  scrollEnabled?: boolean;

  url: string;

  /** ViewStyle: https://reactnative.dev/docs/view-style-props. Supported in release 6.15+ */
  containerStyle?: ViewStyle;

  /**
   * WebView props: https://github.com/react-native-webview/react-native-webview/blob/master/docs/Reference.md.
   * Supported in release 6.15+
   * */
  webViewProps?: Partial<WebViewProps>;

  /** Optional Lottie animation props for custom loading indicator. If not provided, uses default KitLoader. Supported in release 6.15+ */
  loadingIndicatorLottieProps?: LottieViewProps;

  /** Whether to show loading indicator while content is loading. Defaults to true. Supported in release 6.15+ */
  loadingIndicatorEnabled?: boolean;
}

/** Initial support added in release 6.12.0 */
export default function BrowserBlock(
  props: BrowserBlockProps
): JSX.Element | null {
  const {
    height = '100%',
    fitContent = false,
    actionDispatch = false,
    topSpacing = null,
    bottomSpacing = null,
    horizontalSpacing,
    scrollEnabled,
    url,
    containerStyle = {},
    webViewProps = {},
    loadingIndicatorEnabled,
    loadingIndicatorLottieProps,
    insetStyle = {},
    extendTop,
    extendBottom,
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  const { edgeSpacing, viewPortHeight } = useScreenContext({
    fixedSpacingType: horizontalSpacing,
  });

  const { dispatchAction } = useBlockPageContext();

  const viewStyle = {
    ...insetStyle,
    marginTop: getSpacing(topSpacing),
    marginBottom: getSpacing(bottomSpacing),
    marginHorizontal: edgeSpacing,
    ...containerStyle,
  };

  const calculateWebViewHeight = (): number => {
    if (fitContent) {
      if (contentHeight !== null) {
        return contentHeight;
      }

      // Use the height prop as initial height while measuring
      if (typeof height === 'string' && height.endsWith('%')) {
        const percentage = parseInt(height.slice(0, -1), 10);

        if (!isNaN(percentage)) {
          const calculatedHeight = viewPortHeight * (percentage / 100);

          return calculatedHeight;
        }
      }

      return height as number;
    }

    if (typeof height === 'string' && height.endsWith('%')) {
      const percentage = parseInt(height.slice(0, -1), 10);

      if (!isNaN(percentage)) {
        const calculatedHeight = viewPortHeight * (percentage / 100);

        return calculatedHeight;
      }
    }

    return height as number;
  };

  const webViewHeight = calculateWebViewHeight();

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleMessage = (event: any) => {
    if (Platform.OS !== 'web') {
      try {
        const data = JSON.parse(event.nativeEvent.data);

        if (
          fitContent &&
          data.type === 'fitContentHeight' &&
          typeof data.height === 'number'
        ) {
          setContentHeight(data.height);

          return;
        }

        if (actionDispatch && data.type === 'dispatchAction' && data.action) {
          debug('Received action dispatch from WebView:', data.action);
          dispatchAction(data.action);

          return;
        }
      } catch {}
    }
  };

  const containerStyles = {
    ...viewStyle,
    height: webViewHeight,
  };

  const renderLoadingIndicator = () => {
    if (loadingIndicatorLottieProps) {
      return (
        <LottieView
          style={{
            width: 100,
            height: 100,
          }}
          {...loadingIndicatorLottieProps}
        />
      );
    }

    return <KitLoader />;
  };

  const webViewStyle = [
    { width: '100%', height: '100%', backgroundColor: 'clear' },
    webViewProps.style,
  ];

  // Listen for messages from iframe content
  useEffect(() => {
    if (
      Platform.OS === 'web' &&
      typeof window !== 'undefined' &&
      window.addEventListener
    ) {
      const handleMessage = (event: MessageEvent) => {
        // Handle height response from iframe content
        if (
          fitContent &&
          event.data?.type === 'fitContentHeight' &&
          typeof event.data?.height === 'number'
        ) {
          setContentHeight(event.data.height);

          return;
        }

        // Handle action dispatch from iframe content
        if (
          actionDispatch &&
          event.data?.type === 'dispatchAction' &&
          event.data?.action
        ) {
          debug('Received action dispatch from iframe:', event.data.action);
          dispatchAction(event.data.action);

          return;
        }
      };

      window.addEventListener('message', handleMessage);

      return () => {
        if (window.removeEventListener) {
          window.removeEventListener('message', handleMessage);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitContent, actionDispatch]);

  return (
    <View style={containerStyles}>
      <BackgroundExtension
        extendTop={extendTop}
        extendBottom={extendBottom}
        containerStyle={containerStyle}
      />
      <View style={styles.webViewContainer}>
        {Platform.OS === 'web' ? (
          <iframe
            src={url}
            style={{
              width: '100%',
              height: '100%',
              border: 0,
              backgroundColor: 'clear',
              ...webViewProps?.style,
            }}
            onLoad={handleLoad}
          />
        ) : (
          <WebView
            source={{ uri: url }}
            style={webViewStyle}
            scrollEnabled={scrollEnabled}
            onLoadEnd={handleLoad}
            onMessage={handleMessage}
            {...webViewProps}
          />
        )}
        {loadingIndicatorEnabled && isLoading ? (
          <View style={styles.loaderOverlay}>{renderLoadingIndicator()}</View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  webViewContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  loaderOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
